import { SubstepType } from "~/Configurators/Linear/dtos";
import { isNonNull, toMapFn } from "~/utils/reducers";
import type { SummaryData, SummaryDataProvider } from "~/Configurators/Shaft/SummaryDataProvider";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { formatCsticValueAsNumber } from "~/Configurators/Shaft/components/useMinMaxCsticLabel";
import { InputCstics } from "~/Configurators/Rail/components/ConfigureRailSubstep/constants";
import { removeMultipleTags } from "~/utils/domUtils";
import { getMainKmatCstics } from "~/Configurators/common/v2/helper";

export function getStep1SummaryData(summaryProvider: SummaryDataProvider) {
  const substeps = [SubstepType.Accuracy];

  const substepData = substeps
    .map((substepType) => [substepType, summaryProvider.prepareTileSubstepSummary(substepType)] as const)
    .filter(([, summary]) => isNonNull(summary)) as [SubstepType, SummaryData][];

  if (substepData.length === 0) {
    return null;
  }

  const data: Record<string, SummaryData> = toMapFn(
    substepData,
    ([type]) => type,
    ([, summary]) => summary,
  );

  return substeps.map((stepName) => data[stepName]).filter(Boolean) as SummaryData[];
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] {
  const substeps = [SubstepType.Closure];
  const t = summaryProvider.t;

  const summaryData: SummaryData[] = substeps
    .map((substepType) => summaryProvider.prepareTileSubstepSummary(substepType))
    .filter(isNonNull);
  const configureRailSubstep = summaryProvider.substeps[SubstepType.ConfigureRail];
  if (configureRailSubstep) {
    const inputCstics = getMainKmatCstics(configureRailSubstep).input;
    const lengthDef = inputCstics[InputCstics.TotalLength];
    const alDef = inputCstics[InputCstics.aL];
    const arDef = inputCstics[InputCstics.aR];
    const partAmountDef = inputCstics[InputCstics.partAmount];

    [lengthDef, alDef, arDef]
      .filter(Boolean)
      .map((def) => {
        const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
        return cstics[def.name];
      })
      .filter((cstic) => cstic?.value)
      .forEach((cstic) => {
        summaryData.push({
          name: t(cstic.label),
          isStandard: false,
          value: formatNumberValue(cstic.value, "mm"),
        });
      });

    [partAmountDef]
      .filter(Boolean)
      .map((def) => {
        const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
        return cstics[def.name];
      })
      .filter((cstic) => cstic?.value)
      .forEach((cstic) => {
        summaryData.push({
          name: t(cstic.label),
          isStandard: false,
          value: t(cstic.values.find((v) => v.value === cstic.value)?.label ?? ""),
        });
      });
  }

  summaryData.forEach((data) => {
    data.value = removeMultipleTags(data.value, "p", "span")!;
    data.name = removeMultipleTags(data.name, "p", "span")!;
  });

  return summaryData;
}

function formatNumberValue(value: string | undefined, unit?: string) {
  let val = formatCsticValueAsNumber(value, 2);
  if (unit) {
    val += unit;
  }
  return val;
}
